import React from 'react';
import { Grid, Card, CardContent, CardMedia, Typography, Box, IconButton, Chip } from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, LocalShipping as PreorderIcon, ContentCopy as CloneIcon } from '@mui/icons-material';
import { useTranslation } from '../../hooks/useTranslation';
import { format } from 'date-fns';

function ToyCard({ toy, userCurrency, onEdit, onDelete, onClone, getImageUrl }) {
  const { t } = useTranslation();

  const formatDate = (date) => {
    if (!date) return '';
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return '';
    return format(dateObj, 'yyyy/MM/dd');
  };

  return (
    <Grid item xs={12} sm={6} md={4} lg={3}>
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
          cursor: 'pointer',
          position: 'relative',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)'
          }
        }}
      >
        {/* Action buttons overlay */}
        <Box sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 10,
          display: 'flex',
          gap: 0.5
        }}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onClone(toy);
            }}
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.9)',
              '&:hover': { bgcolor: 'white' }
            }}
            title={t('common.clone')}
          >
            <CloneIcon fontSize="small" color="info" />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(toy.id);
            }}
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.9)',
              '&:hover': { bgcolor: 'white' }
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(toy);
            }}
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.9)',
              '&:hover': { bgcolor: 'white' }
            }}
          >
            <DeleteIcon fontSize="small" color="error" />
          </IconButton>
        </Box>

        <Box 
          sx={{ height: 200, position: 'relative', bgcolor: 'grey.100' }}
          onClick={() => onEdit(toy.id)}
        >
          <CardMedia
            component="img"
            height="200"
            image={getImageUrl(toy.imageUrl, toy.name)}
            alt={toy.name}
            sx={{ 
              objectFit: 'cover',
              width: '100%'
            }}
          />
        </Box>
        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
            {toy.name}
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            {toy.brand && (
              <Chip 
                label={toy.brand} 
                size="small" 
                sx={{ mr: 0.5, mb: 0.5 }}
              />
            )}
            {toy.series && (
              <Chip 
                label={toy.series} 
                size="small" 
                variant="outlined"
                sx={{ mr: 0.5, mb: 0.5 }}
              />
            )}
            {toy.type && (
              <Chip 
                label={toy.type} 
                size="small" 
                variant="outlined"
                sx={{ mr: 0.5, mb: 0.5 }}
              />
            )}
            {toy.scale && (
              <Chip 
                label={toy.scale} 
                size="small" 
                variant="outlined"
                sx={{ mr: 0.5, mb: 0.5 }}
              />
            )}
          </Box>

          <Box sx={{ mt: 'auto' }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {toy.code && `${t('common.code')}: ${toy.code}`}
            </Typography>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                {userCurrency} {toy.preorder_price && toy.preorder_price > 0 && !toy.createdDate 
                  ? Math.round(toy.preorder_price).toLocaleString()
                  : Math.round(toy.price || 0).toLocaleString()
                }
              </Typography>
              {toy.preorder_price && toy.preorder_price > 0 && !toy.createdDate && (
                <Chip
                  icon={<PreorderIcon />}
                  label={t('common.preorder')}
                  size="small"
                  color="warning"
                />
              )}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {t('common.quantity')}: {toy.qty || 1}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatDate(toy.createdDate)}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );
}

export default ToyCard; 